"use client";

import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import "./FeedbackForm.css";
import { useEffect, useState } from "react";
import config from "../config";

// Helper to get theme-aware colors
const getThemeColors = () => {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  
  return {
    primary: isDark ? '#e8e8e8' : '#223651',
    secondary: isDark ? '#a0a0a0' : '#6b7280',
    background: isDark ? '#1a1a1a' : '#ffffff',
    mainBackground: isDark ? '#2d2d2d' : '#ffffff', // Main form background - lighter gray
    cardBackground: isDark ? '#242424' : '#f9f9f9', // Question cards - darker than main form
    headerColor: isDark ? '#5a8aff' : '#223651', // Header text color - bright blue
    buttonBg: isDark ? '#5a8aff' : '#223651',
    buttonHover: isDark ? '#4a7aef' : '#1a2a3f',
    border: isDark ? '#4a4a4a' : '#d1d5db',
    borderGrey: isDark ? '#4a4a4a' : '#b6bec6',
    // SurveyJS specific colors
    sjsPrimary: isDark ? '#5a8aff' : '#223651',
    sjsSecondary: isDark ? '#4a7aef' : '#3369ff',
    sjsBackground: isDark ? '#2d2d2d' : '#ffffff',
    sjsBorder: isDark ? '#4a4a4a' : '#b6bec6',
    sjsBorderDefault: isDark ? '#5a5a5a' : '#52555b',
    sjsShadow: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(34, 54, 81, 0.15)'
  };
};

export default function SurveyForm() {
  const [survey, setSurvey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [meetingData, setMeetingData] = useState({});
  const [themeColors, setThemeColors] = useState(getThemeColors());

  // Function to get URL parameters
  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};
    for (const [key, value] of urlParams.entries()) {
      params[key] = value;
    }
    return params;
  };

  // Function to submit survey data
  const submitSurveyData = async (surveyData) => {
    try {
      // Get URL parameters to extract meeting data
      const urlParams = getUrlParams();
      
      // Decode meeting data from URL
      let meetingData = {};
      if (urlParams.d) {  // Changed from 'data' to 'd'
        try {
          const decoded = atob(urlParams.d);  // Changed from 'data' to 'd'
          meetingData = JSON.parse(decoded);
          console.log("Decoded meeting data:", meetingData);
        } catch (error) {
          console.error("Error decoding meeting data:", error);
        }
      } else {
        console.log("No 'd' parameter found in URL params:", urlParams);
      }
      
      // Merge survey data with meeting data (add cp_ prefix)
      const submissionData = { ...surveyData };
      for (const [key, value] of Object.entries(meetingData)) {
        submissionData[`cp_${key}`] = value;
      }
      
      console.log("Final submission data:", submissionData);
      
      const response = await fetch(`${config.api.baseUrl}/cycle-planning/form`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit survey");
      }

      const result = await response.json();
      console.log("Survey submitted successfully:", result);
      return result;
    } catch (error) {
      console.error("Error submitting survey:", error);
      throw error;
    }
  };

  // Function to apply theme to survey
  const applyThemeToSurvey = (surveyModel, colors) => {
    console.log('Applying SurveyJS theme with colors:', colors);
    surveyModel.applyTheme({
      cssVariables: {
        // Primary colors
        "--sjs-primary-backcolor": colors.sjsPrimary,
        "--sjs-primary-forecolor": "#ffffff",
        "--sjs-secondary-backcolor": colors.sjsSecondary,
        "--sjs-secondary-forecolor": "#ffffff",
        
        // General background colors - CRITICAL FOR DARK MODE
        "--sjs-general-backcolor": colors.mainBackground,
        "--sjs-general-backcolor-dark": colors.background,
        "--sjs-general-backcolor-dim": colors.cardBackground,
        "--sjs-general-backcolor-dim-light": colors.cardBackground,
        "--sjs-general-forecolor": colors.primary,
        "--sjs-general-forecolor-light": colors.secondary,
        "--sjs-general-dim-forecolor": colors.secondary,
        "--sjs-general-dim-forecolor-light": colors.secondary,
        
        // Question backgrounds
        "--sjs-question-background": colors.cardBackground,
        
        // Editor (input) backgrounds
        "--sjs-editor-background": colors.background,
        "--sjs-editorpanel-backcolor": colors.background,
        "--sjs-editorpanel-hovercolor": colors.cardBackground,
        
        // Special colors
        "--sjs-special-red": "#f68c1e",
        "--sjs-special-green": "#f68c1e",
        "--sjs-special-blue": colors.sjsSecondary,
        "--sjs-special-yellow": "#f68c1e",
        
        // Borders
        "--sjs-border-light": colors.sjsBorder,
        "--sjs-border-default": colors.sjsBorderDefault,
        "--sjs-border-inside": colors.sjsBorder,
        
        // Styling
        "--sjs-corner-radius": "12px",
        "--sjs-base-unit": "8px",
        "--sjs-shadow-small": `0px 1px 2px 0px ${colors.sjsShadow}`,
        "--sjs-shadow-medium": `0px 2px 6px 0px ${colors.sjsShadow}`,
        "--sjs-shadow-large": `0px 8px 16px 0px ${colors.sjsShadow}`,
        "--sjs-font-family": "system-ui, -apple-system, sans-serif",
        
        // Background aliases
        "--sjs-background": colors.mainBackground,
        "--sjs-background-dim": colors.cardBackground,
        "--sjs-background-dim-light": colors.cardBackground,
      },
    });
  };

  useEffect(() => {
    const initializeSurvey = async () => {
      try {
        // Get URL parameters
        const urlParams = getUrlParams();
        console.log("URL parameters:", urlParams);
        
        // Extract and store meeting data for heading
        let meetingInfo = {};
        if (urlParams.d) {
          try {
            const decoded = atob(urlParams.d);
            meetingInfo = JSON.parse(decoded);
            setMeetingData(meetingInfo);
          } catch (error) {
            console.error("Error decoding meeting data:", error);
          }
        }

        // Build URL with parameters for GET request
        const apiUrl = new URL(`${config.api.baseUrl}/cycle-planning/survey`);
        Object.keys(urlParams).forEach(key => {
          apiUrl.searchParams.append(key, urlParams[key]);
        });

        // Send GET request to backend to get complete survey
        const response = await fetch(apiUrl.toString(), {
          method: "GET",
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch survey");
        }

        const surveyData = await response.json();
        console.log("Survey data received:", surveyData);

        // Create SurveyJS model
        const surveyModel = new Model(surveyData);
        
        // Apply theme based on current theme
        const colors = getThemeColors();
        applyThemeToSurvey(surveyModel, colors);
        
        // Set up form submission
        surveyModel.onComplete.add(async (sender) => {
          try {
            await submitSurveyData(sender.data);
            alert("Survey submitted successfully!");
          } catch (error) {
            alert("Error submitting survey: " + error.message);
          }
        });

        setSurvey(surveyModel);
        
        // Force header color immediately after survey loads
        setTimeout(() => {
          const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
          if (isDark) {
            const container = document.querySelector('.feedback-form-container');
            if (container) {
              const headerElements = container.querySelectorAll('.sv_header, .sv_header *, .sv_title, .sv_title *, .sv_page_title, .sv_page_title *, h3, h4, h5');
              headerElements.forEach(el => {
                if (el.style.color) {
                  el.style.removeProperty('color');
                }
                el.style.setProperty('color', '#5a8aff', 'important');
              });
              console.log(`Initial header color override: ${headerElements.length} elements set to #5a8aff`);
            }
          }
        }, 100);
      } catch (error) {
        console.error("Error initializing survey:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSurvey();
  }, []);

  // Force override any remaining backgrounds with JavaScript
  useEffect(() => {
    if (!survey) return;

    const forceBackgroundColors = () => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      if (!isDark) return;

      // Scope to feedback form container only
      const container = document.querySelector('.feedback-form-container');
      if (!container) return;

      console.log('Force overriding backgrounds with JavaScript (scoped to feedback form)...');

      // Force header text color to bright blue
      const headerElements = container.querySelectorAll('.sv_header, .sv_header *, .sv_title, .sv_title *, .sv_page_title, .sv_page_title *, h3, h4, h5');
      headerElements.forEach(el => {
        // Remove inline color style if it exists
        if (el.style.color) {
          el.style.removeProperty('color');
        }
        // Set to brand blue with !important
        el.style.setProperty('color', '#5a8aff', 'important');
      });
      console.log(`Forced ${headerElements.length} header elements to #5a8aff`);

      // Helper to check if color is light (brightness > 50%)
      const isLightColor = (rgbString) => {
        if (!rgbString || rgbString === 'transparent' || rgbString === 'rgba(0, 0, 0, 0)') {
          return false; // Treat transparent as not light
        }
        const match = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (!match) return false;
        const [, r, g, b] = match.map(Number);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 128; // If brightness > 128 (out of 255), it's a light color
      };

      // Force all elements with light backgrounds to be dark - SCOPED TO CONTAINER
      const allElements = container.querySelectorAll('.sv_main, .sv_main *, .sv_body, .sv_body *, .sv_q, .sv_q *, .sv_container, .sv_container *, .sv_page, .sv_page *');
      let count = 0;
      let lightBackgrounds = [];
      
      allElements.forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        const bgColor = computedStyle.backgroundColor;
        
        // If background is light, force it to dark
        if (isLightColor(bgColor)) {
          lightBackgrounds.push({ element: el.className, color: bgColor });
          
          // Inputs get darkest background
          if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
            el.style.setProperty('background-color', '#1a1a1a', 'important');
            el.style.setProperty('background', '#1a1a1a', 'important');
            el.style.setProperty('color', '#e8e8e8', 'important');
            count++;
          }
          // Main containers get main background (lighter gray)
          else if (el.classList.contains('sv_main') || el.classList.contains('sv_body') || 
                   el.classList.contains('sv_container') || el.classList.contains('sv_page') ||
                   el.classList.contains('sv_header') || el.classList.contains('sd-root-modern')) {
            el.style.setProperty('background-color', '#2d2d2d', 'important');
            el.style.setProperty('background', '#2d2d2d', 'important');
            count++;
          }
          // Question cards and their children get darker background
          else if (el.classList.contains('sv_q') || el.classList.contains('sv_qstn') || 
                   el.classList.contains('sv-question') || el.classList.contains('sv_row') ||
                   el.closest('.sv_q') || el.closest('.sv_qstn') || el.closest('.sv-question')) {
            el.style.setProperty('background-color', '#242424', 'important');
            el.style.setProperty('background', '#242424', 'important');
            count++;
          }
          // Everything else gets card background
          else {
            el.style.setProperty('background-color', '#242424', 'important');
            el.style.setProperty('background', '#242424', 'important');
            count++;
          }
        }
      });
      
      console.log(`Fixed ${count} elements with light backgrounds`);
      if (lightBackgrounds.length > 0) {
        console.log('Light backgrounds found:', lightBackgrounds.slice(0, 10)); // Show first 10
      }
      
      // Also fix text colors - SCOPED TO CONTAINER
      const textElements = container.querySelectorAll('.sv_main *, .sv_body *, .sv_q *, .sv-string-viewer, .sv-title, .sv-question__title');
      textElements.forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        const textColor = computedStyle.color;
        
        // If text is too light or too dark for dark mode, fix it
        const match = textColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (match) {
          const [, r, g, b] = match.map(Number);
          const brightness = (r * 299 + g * 587 + b * 114) / 1000;
          
          // If text is too dark (brightness < 100), make it light
          if (brightness < 100) {
            el.style.setProperty('color', '#e8e8e8', 'important');
          }
        }
      });
    };

    // Run immediately
    forceBackgroundColors();

    // Run again after survey renders
    const timer = setTimeout(forceBackgroundColors, 100);
    const timer2 = setTimeout(forceBackgroundColors, 500);
    const timer3 = setTimeout(forceBackgroundColors, 1000);

    // Set up MutationObserver to catch dynamically added elements
    const observer = new MutationObserver((mutations) => {
      forceBackgroundColors();
    });

    const surveyElement = document.querySelector('.sv_main');
    if (surveyElement) {
      observer.observe(surveyElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      clearTimeout(timer3);
      observer.disconnect();
    };
  }, [survey, themeColors]);

  // Listen for theme changes
  useEffect(() => {
    const updateTheme = () => {
      const newColors = getThemeColors();
      console.log('FeedbackForm: Theme changed to', document.documentElement.getAttribute('data-theme'), newColors);
      setThemeColors(newColors);
      
      // Reapply theme to survey if it exists
      if (survey) {
        applyThemeToSurvey(survey, newColors);
        // Force survey to re-render
        survey.render();
      }
    };

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    // Also log initial state
    console.log('FeedbackForm: Initial theme', document.documentElement.getAttribute('data-theme'), themeColors);

    return () => observer.disconnect();
  }, [survey]);

  // Inject dynamic styles into document head
  useEffect(() => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    const styleId = 'feedback-form-dynamic-styles';
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    
    console.log('FeedbackForm: Injecting styles for theme:', isDark ? 'DARK' : 'LIGHT', themeColors);
    
    styleElement.textContent = `
      /* Dynamic theme styles for feedback form - SCOPED */
      .feedback-form-container .sv_main {
        background: ${themeColors.mainBackground} !important;
        border-radius: 12px !important;
        box-shadow: 0 4px 6px ${themeColors.sjsShadow} !important;
      }
      
      /* Headers - use main background with brand color - ULTRA SPECIFIC */
      .feedback-form-container .sv_header,
      .feedback-form-container .sv_header *,
      .feedback-form-container .sv_header h1,
      .feedback-form-container .sv_header h2,
      .feedback-form-container .sv_header h3,
      .feedback-form-container .sv_header h4,
      .feedback-form-container .sv_header h5,
      .feedback-form-container .sv-root-modern__header,
      .feedback-form-container .sv-root-modern__header *,
      .feedback-form-container .sv_title,
      .feedback-form-container .sv_title *,
      .feedback-form-container .sv_page_title,
      .feedback-form-container .sv_page_title * {
        background: ${themeColors.mainBackground} !important;
        background-color: ${themeColors.mainBackground} !important;
        color: ${themeColors.headerColor} !important;
      }
      
      /* Question cards - darker than main form - SCOPED */
      .feedback-form-container .sv_qstn,
      .feedback-form-container .sv-question,
      .feedback-form-container .sv-element,
      .feedback-form-container .sv_q,
      .feedback-form-container .sv_q.sv_q {
        background: ${themeColors.cardBackground} !important;
        background-color: ${themeColors.cardBackground} !important;
        border-radius: 8px !important;
        padding: 16px !important;
        margin-bottom: 12px !important;
      }
      
      /* All question card children should inherit card background - SCOPED */
      .feedback-form-container .sv-question__content,
      .feedback-form-container .sv-question__header,
      .feedback-form-container .sv-element__content,
      .feedback-form-container .sv_q_content,
      .feedback-form-container .sv_q > div:not(input):not(textarea):not(select),
      .feedback-form-container .sv_q_radiogroup,
      .feedback-form-container .sv_q_checkbox,
      .feedback-form-container .sv_q_rating {
        background: ${themeColors.cardBackground} !important;
        background-color: ${themeColors.cardBackground} !important;
      }
      
      .feedback-form-container .sv_q_title,
      .feedback-form-container .sv_q_title.sv_q_title {
        color: ${themeColors.primary} !important;
      }
      
      .feedback-form-container .sv_q_description,
      .feedback-form-container .sv_q_description.sv_q_description {
        color: ${themeColors.secondary} !important;
      }
      
      .feedback-form-container .sv_q_text input,
      .feedback-form-container .sv_q_comment textarea {
        padding: 8px 12px !important;
        font-size: 14px !important;
        border: 1px solid ${themeColors.border} !important;
        border-radius: 6px !important;
        background-color: ${themeColors.background} !important;
        color: ${themeColors.primary} !important;
      }
      
      .feedback-form-container .sv_btn,
      .feedback-form-container .sv_btn.sv_btn {
        background-color: ${themeColors.buttonBg} !important;
        color: white !important;
        border: 1px solid ${themeColors.buttonBg} !important;
      }
      
      .feedback-form-container .sv_btn:hover,
      .feedback-form-container .sv_btn.sv_btn:hover {
        background-color: ${themeColors.buttonHover} !important;
        border-color: ${themeColors.buttonHover} !important;
      }
      
      /* All main containers - use main background - SCOPED */
      .feedback-form-container .sv_body,
      .feedback-form-container .sv_container,
      .feedback-form-container .sv_page,
      .feedback-form-container .sv_row,
      .feedback-form-container .sv_p_root,
      .feedback-form-container .sv-page,
      .feedback-form-container .sv-page__content,
      .feedback-form-container .sv-card,
      .feedback-form-container .sv-root-modern__container {
        background: ${themeColors.mainBackground} !important;
        background-color: ${themeColors.mainBackground} !important;
      }
      
      /* All inputs - use darker background to stand out from cards - SCOPED */
      .feedback-form-container input[type="text"],
      .feedback-form-container input[type="number"],
      .feedback-form-container input[type="email"],
      .feedback-form-container input[type="tel"],
      .feedback-form-container textarea,
      .feedback-form-container select,
      .feedback-form-container input:not([type="radio"]):not([type="checkbox"]) {
        background-color: ${themeColors.background} !important;
        color: ${themeColors.primary} !important;
        border-color: ${themeColors.border} !important;
      }
      
      /* Labels - SCOPED */
      .feedback-form-container label,
      .feedback-form-container .sv_q_radiogroup_label,
      .feedback-form-container .sv_q_checkbox_label {
        color: ${themeColors.primary} !important;
      }
    `;
    
    return () => {
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, [themeColors]);

  if (isLoading) {
    console.log('FeedbackForm LOADING: mainBackground =', themeColors.mainBackground);
    return (
      <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: themeColors.mainBackground, color: themeColors.primary, minHeight: '100vh' }}>
        <div>Loading form...</div>
      </div>
    );
  }

  if (!survey) {
    console.log('FeedbackForm ERROR: mainBackground =', themeColors.mainBackground);
    return (
      <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: themeColors.mainBackground, color: themeColors.primary, minHeight: '100vh' }}>
        <div>Error loading form</div>
      </div>
    );
  }

  console.log('FeedbackForm RENDER: mainBackground =', themeColors.mainBackground, 'cardBackground =', themeColors.cardBackground);
  
  return (
    <div className="min-h-screen feedback-form-container" style={{ backgroundColor: themeColors.mainBackground }}>
      <style dangerouslySetInnerHTML={{__html: `
        /* ULTRA-AGGRESSIVE: Force everything to be transparent first */
        .sv_main *,
        .sv_body *,
        .sv_container *,
        .sv_page *,
        .sv_q * {
          background: transparent !important;
          background-color: transparent !important;
        }
        
        /* Main container - use main background */
        .sv_main,
        .sv_main.sv_main {
          background: ${themeColors.mainBackground} !important;
          background-color: ${themeColors.mainBackground} !important;
          border-radius: 12px !important;
          box-shadow: 0 4px 6px ${themeColors.sjsShadow} !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        
        /* Question frames get card background for contrast */
        .sv_q,
        .sv_q.sv_q,
        .sv_main .sv_q,
        .sv_body .sv_q,
        .sv_page .sv_q,
        .sv_qstn {
          margin-bottom: 12px !important;
          padding: 16px !important;
          background: ${themeColors.cardBackground} !important;
          background-color: ${themeColors.cardBackground} !important;
          border: none !important;
          border-width: 0 !important;
          box-shadow: none !important;
          border-radius: 8px !important;
          outline: none !important;
        }
        
        /* Remove any SurveyJS default question styling */
        .sv_q_required,
        .sv_q_required.sv_q_required {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        
        /* Question content should inherit card background */
        .sv_q_content,
        .sv_q_content.sv_q_content {
          background: ${themeColors.cardBackground} !important;
          border: none !important;
          box-shadow: none !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        
        /* Compact title styling */
        .sv_q_title,
        .sv_q_title.sv_q_title {
          font-size: 16px !important;
          font-weight: 600 !important;
          margin-bottom: 8px !important;
          color: ${themeColors.primary} !important;
          line-height: 1.4 !important;
        }
        
        /* Compact description styling */
        .sv_q_description,
        .sv_q_description.sv_q_description {
          font-size: 14px !important;
          color: ${themeColors.secondary} !important;
          margin-bottom: 8px !important;
          line-height: 1.4 !important;
        }
        
        /* Compact input styling */
        .sv_q_radiogroup,
        .sv_q_radiogroup.sv_q_radiogroup,
        .sv_q_checkbox,
        .sv_q_checkbox.sv_q_checkbox,
        .sv_q_rating,
        .sv_q_rating.sv_q_rating,
        .sv_q_text,
        .sv_q_text.sv_q_text,
        .sv_q_comment,
        .sv_q_comment.sv_q_comment {
          margin-top: 6px !important;
          padding: 0 !important;
        }
        
        /* Compact radio/checkbox items */
        .sv_q_radiogroup .sv_q_radiogroup_item,
        .sv_q_checkbox .sv_q_checkbox_item {
          margin-bottom: 6px !important;
          padding: 4px 0 !important;
        }
        
        /* Compact rating items */
        .sv_q_rating .sv_q_rating_item {
          margin: 0 4px !important;
          padding: 4px !important;
        }
        
        /* Compact text inputs */
        .sv_q_text input,
        .sv_q_comment textarea {
          padding: 8px 12px !important;
          font-size: 14px !important;
          border: 1px solid ${themeColors.border} !important;
          border-radius: 6px !important;
          background-color: ${themeColors.background} !important;
          color: ${themeColors.primary} !important;
        }
        
        /* Compact buttons with brand colors */
        .sv_btn,
        .sv_btn.sv_btn {
          padding: 10px 20px !important;
          font-size: 14px !important;
          border-radius: 6px !important;
          margin-top: 16px !important;
          background-color: ${themeColors.buttonBg} !important;
          color: white !important;
          border: 1px solid ${themeColors.buttonBg} !important;
        }
        
        .sv_btn:hover,
        .sv_btn.sv_btn:hover {
          background-color: ${themeColors.buttonHover} !important;
          border-color: ${themeColors.buttonHover} !important;
        }
        
        /* Question input wrappers should inherit card background */
        .sv_q_comment,
        .sv_q_dropdown,
        .sv_q_matrix,
        .sv_q_boolean,
        .sv_q_file,
        .sv_q_signaturepad,
        .sv_q_ranking,
        .sv_q_imagepicker,
        .sv_q_multipletext,
        .sv_q_expression,
        .sv_q_html,
        .sv_q_radiogroup,
        .sv_q_checkbox,
        .sv_q_rating,
        .sv_q_text {
          background: ${themeColors.cardBackground} !important;
          background-color: ${themeColors.cardBackground} !important;
          border: none !important;
          border-width: 0 !important;
          box-shadow: none !important;
          margin-top: 6px !important;
          padding: 0 !important;
          outline: none !important;
        }
        
        /* Override SurveyJS default question container styling */
        .sv_main .sv_q,
        .sv_main .sv_q_radiogroup,
        .sv_main .sv_q_checkbox,
        .sv_main .sv_q_rating,
        .sv_main .sv_q_text,
        .sv_main .sv_q_comment {
          background: ${themeColors.cardBackground} !important;
          border: none !important;
          box-shadow: none !important;
          border-radius: 0 !important;
        }
        
        /* Remove any SurveyJS theme borders */
        .sv_q[data-sv-question-type],
        .sv_q[data-sv-question-type] * {
          border: none !important;
          box-shadow: none !important;
        }
        
        /* Force all SurveyJS main containers to use main background */
        .sv_body,
        .sv_body.sv_body,
        .sv_container,
        .sv_container.sv_container,
        .sv_page,
        .sv_page.sv_page,
        .sv_row,
        .sv_row.sv_row {
          background: ${themeColors.mainBackground} !important;
          background-color: ${themeColors.mainBackground} !important;
        }
        
        /* Force all input-like elements to have proper background */
        .sv_q_text input[type="text"],
        .sv_q_text input[type="number"],
        .sv_q_text input[type="email"],
        .sv_q_text input[type="tel"],
        .sv_q_comment textarea,
        .sv_q_dropdown select,
        .sv_select_wrapper,
        input[type="text"],
        input[type="number"],
        input[type="email"],
        input[type="tel"],
        textarea,
        select {
          background-color: ${themeColors.background} !important;
          color: ${themeColors.primary} !important;
          border-color: ${themeColors.border} !important;
        }
        
        /* Radio button and checkbox labels */
        .sv_q_radiogroup_label,
        .sv_q_checkbox_label,
        .sv-item__control-label,
        label {
          color: ${themeColors.primary} !important;
        }
        
        /* Rating items */
        .sv_q_rating_item,
        .sv_q_rating_item_text,
        .sv-rating__item,
        .sv-rating__item-text {
          color: ${themeColors.primary} !important;
        }
        
        /* Matrix table */
        .sv_q_matrix,
        .sv_q_matrix table,
        .sv_q_matrix th,
        .sv_q_matrix td {
          background-color: ${themeColors.background} !important;
          color: ${themeColors.primary} !important;
          border-color: ${themeColors.border} !important;
        }
        
        /* Panel containers */
        .sv_panel,
        .sv_panel.sv_panel {
          background-color: ${themeColors.background} !important;
          border-color: ${themeColors.border} !important;
        }
        
        /* Progress bar */
        .sv_progress,
        .sv_progress_bar {
          background-color: ${themeColors.background} !important;
        }
        
        /* Header with brand color - ULTRA SPECIFIC */
        .feedback-form-container .sv_header,
        .feedback-form-container .sv_header *,
        .feedback-form-container .sv_header h1,
        .feedback-form-container .sv_header h2,
        .feedback-form-container .sv_header h3,
        .feedback-form-container .sv_header h4,
        .feedback-form-container .sv_header h5,
        .feedback-form-container .sv_title,
        .feedback-form-container .sv_title *,
        .feedback-form-container .sv_page_title,
        .feedback-form-container .sv_page_title * {
          color: ${themeColors.headerColor} !important;
          background: transparent !important;
        }
        
        /* Footer */
        .sv_footer {
          background-color: ${themeColors.background} !important;
        }
        
        /* Force all inputs to have proper background */
        .sv_main input,
        .sv_main textarea,
        .sv_main select,
        .sv_main input:not([type="radio"]):not([type="checkbox"]),
        input[type="text"],
        input[type="number"],
        input[type="email"],
        input[type="tel"],
        textarea,
        select {
          background-color: ${themeColors.background} !important;
          color: ${themeColors.primary} !important;
        }
        
        /* Ensure select dropdowns and their options have correct colors */
        select option {
          background-color: ${themeColors.background} !important;
          color: ${themeColors.primary} !important;
        }
        
        /* Placeholder text */
        input::placeholder,
        textarea::placeholder {
          color: ${themeColors.secondary} !important;
          opacity: 0.7;
        }
        
        /* Focus states */
        input:focus,
        textarea:focus,
        select:focus {
          outline: none !important;
          border-color: ${themeColors.buttonBg} !important;
        }
      `}} />
      <Survey 
        model={survey} 
        key={`survey-${themeColors.background}`}
      />
    </div>
  );
}